export type Language = 'ar' | 'en';

export type Feature = {
  icon: string;
  title: string;
  description: string;
  points: string[];
};

export type Plan = {
  id: 'essential' | 'professional' | 'enterprise';
  name: string;
  audience: string;
  description: string;
  features: string[];
  featured?: boolean;
  /** Monthly and yearly price in whole currency units. Omitted for custom (enterprise) plans. */
  price?: { monthly: number; yearly: number };
};

type SiteCopy = {
  brandTagline: string;
  nav: { label: string; href: string }[];
  headerCta: string;
  hero: {
    eyebrow: string;
    titleBefore: string;
    titleAccent: string;
    titleAfter: string;
    description: string;
    primaryCta: string;
    secondaryCta: string;
    note: string;
  };
  trust: { value: string; label: string }[];
  features: {
    eyebrow: string;
    title: string;
    description: string;
    items: Feature[];
  };
  showcase: {
    eyebrow: string;
    title: string;
    description: string;
    tabs: string[];
    bullets: string[];
    cta: string;
  };
  chart: {
    eyebrow: string;
    title: string;
    description: string;
    points: { icon: string; title: string; description: string }[];
    hint: string;
    unitLabel: string;
    restorations: string[];
  };
  team: {
    eyebrow: string;
    title: string;
    description: string;
    steps: { icon: string; role: string; action: string }[];
  };
  workflow: {
    eyebrow: string;
    title: string;
    description: string;
    steps: { number: string; title: string; description: string }[];
  };
  security: {
    eyebrow: string;
    title: string;
    description: string;
    points: { icon: string; title: string; description: string }[];
  };
  pricing: {
    eyebrow: string;
    title: string;
    description: string;
    featuredLabel: string;
    quote: string;
    quoteNote: string;
    currency: string;
    perMonth: string;
    annualNote: string;
    cta: string;
    compare: string;
    plans: Plan[];
  };
  purchase: {
    title: string;
    description: string;
    steps: { icon: string; title: string; description: string }[];
  };
  faq: {
    eyebrow: string;
    title: string;
    description: string;
    items: { question: string; answer: string }[];
  };
  finalCta: {
    eyebrow: string;
    title: string;
    description: string;
    primary: string;
    secondary: string;
  };
  footer: {
    description: string;
    product: string;
    company: string;
    legal: string;
    links: string[];
    copyright: string;
    status: string;
  };
  form: {
    eyebrow: string;
    title: string;
    description: string;
    selectedPlan: string;
    name: string;
    lab: string;
    email: string;
    phone: string;
    country: string;
    users: string;
    notes: string;
    privacy: string;
    submit: string;
    sending: string;
    close: string;
    successTitle: string;
    successBody: string;
    successBodyApi: string;
    error: string;
  };
};

export const copy: Record<Language, SiteCopy> = {
  ar: {
    brandTagline: 'نظام إدارة مختبرات الأسنان',
    nav: [
      { label: 'المميزات', href: '/features' },
      { label: 'كيف يعمل', href: '/workflow' },
      { label: 'الأمان', href: '/security' },
      { label: 'الخطط', href: '/pricing' },
      { label: 'الأسئلة الشائعة', href: '/faq' },
    ],
    headerCta: 'اطلب عرضًا',
    hero: {
      eyebrow: 'منصة واحدة. رؤية أوضح. مختبر أقوى.',
      titleBefore: 'إدارة مختبر الأسنان',
      titleAccent: 'بذكاء واحتراف',
      titleAfter: '',
      description:
        'اربط التركيبات والحسابات والمخزون والفريق في نظام واحد صُمم لواقع مختبرات الأسنان — من استلام الحالة حتى التسليم والتحصيل.',
      primaryCta: 'اطلب عرضًا مخصصًا',
      secondaryCta: 'استكشف النظام',
      note: 'إعداد موجّه • نقل بيانات • دعم فني',
    },
    trust: [
      { value: 'محلي + سحابي', label: 'مرونة في طريقة التشغيل' },
      { value: 'صلاحيات دقيقة', label: 'تحكم حسب دور كل مستخدم' },
      { value: 'تحديث لحظي', label: 'فريقك يرى نفس البيانات' },
      { value: 'عربي أولًا', label: 'تجربة واضحة وسريعة' },
    ],
    features: {
      eyebrow: 'كل ما يحتاجه المختبر',
      title: 'نظام واحد بدل الأدوات المتفرقة',
      description:
        'وحدات مترابطة تمنحك صورة كاملة للعمل، وتقلل الإدخال المتكرر، وتساعد فريقك على إنجاز الحالات بثقة.',
      items: [
        {
          icon: 'tooth',
          title: 'إدارة التركيبات',
          description: 'تابع كل حالة منذ الاستلام وحتى التسليم ضمن دورة عمل واضحة.',
          points: ['الحالة والمواعيد', 'الطبيب والمريض', 'الإسناد للفنيين'],
        },
        {
          icon: 'invoice',
          title: 'الفواتير والحسابات',
          description: 'اعرف المستحق والمدفوع وحركة كل حساب بدون جداول منفصلة.',
          points: ['فواتير احترافية', 'دفعات وأرصدة', 'تقارير مالية'],
        },
        {
          icon: 'inventory',
          title: 'المخزون والمشتريات',
          description: 'راقب المواد والحركة والموردين قبل أن تؤثر النواقص على الإنتاج.',
          points: ['تنبيهات المخزون', 'مشتريات الموردين', 'حركة المواد'],
        },
        {
          icon: 'team',
          title: 'الفريق والصلاحيات',
          description: 'امنح كل مستخدم ما يحتاجه فقط، مع متابعة الأداء والمسؤوليات.',
          points: ['أدوار مخصصة', 'حضور ورواتب', 'عمولات الفنيين'],
        },
        {
          icon: 'reports',
          title: 'تقارير تساعدك على القرار',
          description: 'حوّل حركة المختبر اليومية إلى مؤشرات عملية قابلة للفهم.',
          points: ['الأداء والإنتاج', 'المبيعات والتحصيل', 'تصدير وطباعة'],
        },
        {
          icon: 'devices',
          title: 'مكتب وموبايل',
          description: 'ابقَ قريبًا من سير العمل داخل المختبر وخارجه بأمان.',
          points: ['تطبيق مكتبي', 'وصول للموبايل', 'مزامنة لحظية'],
        },
      ],
    },
    showcase: {
      eyebrow: 'مركز قيادة المختبر',
      title: 'كل التفاصيل المهمة، بدون ضوضاء',
      description:
        'لوحة واضحة تضع الحالات العاجلة، التحصيل، الإنتاج وأداء الفريق أمامك لحظة بلحظة.',
      tabs: ['لوحة التحكم', 'التركيبات', 'الحسابات', 'المخزون'],
      bullets: ['رؤية يومية فورية', 'بحث وفلاتر سريعة', 'تنبيهات في الوقت المناسب'],
      cta: 'شاهد المميزات بالتفصيل',
    },
    chart: {
      eyebrow: 'خريطة الأسنان',
      title: 'دقة على مستوى السن الواحد',
      description:
        'كل حالة تُسجَّل على خريطة أسنان بترقيم FDI المعتمد: نوع التركيبة واللون والملاحظة مرتبطة بالسن نفسه، بدل ملاحظات مبعثرة في خانة نصية واحدة.',
      points: [
        { icon: 'tooth', title: 'ترقيم FDI كامل', description: 'الفك العلوي والسفلي بترقيم يفهمه كل طبيب أسنان.' },
        { icon: 'reports', title: 'تفاصيل لكل سن', description: 'النوع واللون والملاحظة محفوظة على السن المحدد.' },
        { icon: 'invoice', title: 'تسعير مرتبط', description: 'عدد الوحدات ينعكس مباشرة على الفاتورة بدون حساب يدوي.' },
      ],
      hint: 'مرّر فوق أي سن',
      unitLabel: 'وحدة',
      restorations: ['زيركون', 'E-max', 'معدن بورسلين', 'قشرة تجميلية'],
    },
    team: {
      eyebrow: 'تعاون بلا فجوات',
      title: 'نظم عمل بين الفريق',
      description:
        'تعاون سلس بين طبيب الأسنان وفريق المعمل، مع متابعة كل مرحلة من إرسال الطلب حتى التسليم — الجميع يعمل على نفس السجل.',
      steps: [
        { icon: 'users', role: 'طبيب الأسنان', action: 'إرسال الطلب' },
        { icon: 'reports', role: 'إدارة المعمل', action: 'استلام ومراجعة' },
        { icon: 'tooth', role: 'الفني', action: 'تصميم وتصنيع' },
        { icon: 'inventory', role: 'التوصيل', action: 'جاهز للتسليم' },
      ],
    },
    workflow: {
      eyebrow: 'دورة عمل متصلة',
      title: 'من استلام الحالة إلى تسليمها بثقة',
      description: 'كل خطوة تحفظ سياقها وتصل تلقائيًا إلى الشخص المناسب داخل المختبر.',
      steps: [
        { number: '01', title: 'تسجيل الحالة', description: 'بيانات الطبيب والمريض والنوع والموعد في نموذج واضح.' },
        { number: '02', title: 'تخطيط وإسناد', description: 'توزيع العمل على الفنيين ومتابعة الأولوية والموعد.' },
        { number: '03', title: 'تنفيذ ومراجعة', description: 'تحديث حالة الإنتاج وإرفاق الصور وتوثيق الملاحظات.' },
        { number: '04', title: 'تسليم وتحصيل', description: 'فاتورة دقيقة، دفعة موثقة ورصيد محدث تلقائيًا.' },
      ],
    },
    security: {
      eyebrow: 'مصمم للعمل الحقيقي',
      title: 'بيانات المختبر تحت سيطرتك',
      description:
        'بنية خادم وعميل، صلاحيات حسب الدور، واتصالات مشفرة تضع الاستمرارية والخصوصية في صلب النظام.',
      points: [
        { icon: 'shield', title: 'صلاحيات حسب الدور', description: 'تحكم تفصيلي فيما يستطيع كل مستخدم رؤيته أو تغييره.' },
        { icon: 'server', title: 'خادم مركزي', description: 'مصدر بيانات واحد يمنع تضارب النسخ بين أجهزة الفريق.' },
        { icon: 'lock', title: 'اتصال آمن', description: 'دعم HTTPS ومسارات وصول محمية للعمل داخل وخارج المختبر.' },
        { icon: 'backup', title: 'استعداد تشغيلي', description: 'أدوات للنسخ الاحتياطي والتحديث وتقارير الأعطال.' },
      ],
    },
    pricing: {
      eyebrow: 'خطط مرنة',
      title: 'ابدأ بما يناسب حجم مختبرك',
      description:
        'نحدد السعر النهائي بعد معرفة عدد الأجهزة والفروع واحتياجات نقل البيانات والدعم. لا رسوم مخفية ولا وعود مبهمة.',
      featuredLabel: 'الأنسب للمختبرات النامية',
      quote: 'عرض سعر مخصص',
      quoteNote: 'حسب الحجم وطريقة التشغيل',
      currency: 'ج.م',
      perMonth: 'شهريًا',
      annualNote: 'وفّر شهرين',
      cta: 'اطلب هذه الخطة',
      compare: 'تحتاج مقارنة تفصيلية؟ تواصل معنا',
      plans: [
        {
          id: 'essential',
          name: 'الأساسية',
          audience: 'لمختبر صغير',
          description: 'ابدأ بتنظيم الحالات والحسابات اليومية من مكان واحد.',
          features: ['إدارة التركيبات', 'العملاء والفواتير', 'تقارير أساسية', 'حتى 5 مستخدمين', 'إعداد وتدريب أولي'],
          price: { monthly: 800, yearly: 8000 },
        },
        {
          id: 'professional',
          name: 'الاحترافية',
          audience: 'لمختبر نامٍ',
          description: 'اربط التشغيل والموارد والفريق مع رؤية إدارية أعمق.',
          features: ['كل مميزات الأساسية', 'المخزون والمشتريات', 'الموظفون والعمولات', 'صلاحيات متقدمة', 'حتى 15 مستخدمًا', 'وصول الموبايل'],
          featured: true,
          price: { monthly: 2000, yearly: 20000 },
        },
        {
          id: 'enterprise',
          name: 'المؤسسات',
          audience: 'للفروع والفرق الكبيرة',
          description: 'تهيئة ونشر ودعم يناسب العمليات متعددة المواقع.',
          features: ['كل مميزات الاحترافية', 'تهيئة متعددة الفروع', 'نقل بيانات مخصص', 'دعم ذو أولوية', 'تكاملات حسب الحاجة'],
        },
      ],
    },
    purchase: {
      title: 'رحلة شراء واضحة وآمنة',
      description: 'لن نطلب بيانات بطاقة قبل تفعيل مزود دفع معتمد. تبدأ الرحلة بعرض واضح يناسب احتياجك الحقيقي.',
      steps: [
        { icon: 'plan', title: 'اختر الخطة', description: 'حدد الحجم والاحتياجات' },
        { icon: 'chat', title: 'راجع العرض', description: 'سعر ونطاق بلا مفاجآت' },
        { icon: 'payment', title: 'ادفع بأمان', description: 'عبر مزود دفع معتمد' },
        { icon: 'key', title: 'استلم وفعّل', description: 'ترخيص وإعداد موجّه' },
      ],
    },
    faq: {
      eyebrow: 'قبل أن تبدأ',
      title: 'إجابات مباشرة على الأسئلة المهمة',
      description: 'إذا كان سؤالك مختلفًا، أرسله لنا وسنبني العرض حول واقع مختبرك.',
      items: [
        {
          question: 'هل يعمل النظام بدون إنترنت؟',
          answer: 'يمكن تشغيل النظام داخل شبكة المختبر عبر خادم محلي. بعض المزايا الخارجية مثل الوصول عن بعد والتحديثات تحتاج اتصالًا بالإنترنت.',
        },
        {
          question: 'هل يمكن نقل بياناتنا الحالية؟',
          answer: 'نعم. نراجع شكل البيانات أولًا ثم نحدد نطاق النقل والتحقق منها ضمن عرض التنفيذ، حتى لا نفقد تاريخ العمل.',
        },
        {
          question: 'هل يدعم أكثر من جهاز ومستخدم؟',
          answer: 'نعم. بُني النظام على نموذج خادم وعميل ليتشارك الفريق نفس البيانات مع صلاحيات مناسبة لكل دور.',
        },
        {
          question: 'كيف يتم الترخيص والتفعيل؟',
          answer: 'بعد إتمام الاتفاق والدفع يصدر ترخيص موقّع للخطة المتفق عليها، ثم يُفعّل على خادم المختبر عبر مسار آمن.',
        },
        {
          question: 'هل توجد نسخة تجريبية؟',
          answer: 'يمكن ترتيب تجربة موجهة أو ترخيص تجريبي حسب جاهزية بيئة المختبر ونطاق الاستخدام المطلوب.',
        },
      ],
    },
    finalCta: {
      eyebrow: 'لنبدأ من واقع مختبرك',
      title: 'حوّل يوم العمل من رد فعل إلى نظام واضح',
      description: 'أخبرنا بعدد المستخدمين والفروع وطريقة عملك الحالية، وسنقترح خطة تنفيذ مناسبة.',
      primary: 'اطلب عرضًا مخصصًا',
      secondary: 'راسل فريق المبيعات',
    },
    footer: {
      description: 'منصة تشغيل متكاملة لمختبرات الأسنان تجمع الإنتاج والمال والمواد والفريق في تجربة واحدة.',
      product: 'المنتج',
      company: 'التواصل',
      legal: 'قانوني',
      links: ['المميزات', 'كيف يعمل', 'الخطط', 'الأسئلة الشائعة'],
      copyright: 'جميع الحقوق محفوظة.',
      status: 'أنظمة التشغيل متاحة',
    },
    form: {
      eyebrow: 'طلب مبيعات',
      title: 'لنصمم الخطة المناسبة لمختبرك',
      description: 'أرسل معلومات أساسية وسيتواصل معك الفريق لمراجعة المتطلبات والسعر وخطوات التنفيذ.',
      selectedPlan: 'الخطة المختارة',
      name: 'الاسم الكامل',
      lab: 'اسم المختبر',
      email: 'البريد الإلكتروني',
      phone: 'رقم الهاتف',
      country: 'الدولة',
      users: 'عدد المستخدمين المتوقع',
      notes: 'ما أهم شيء تريد تحسينه؟',
      privacy: 'بإرسال الطلب أنت توافق على استخدام هذه البيانات للتواصل بخصوص المنتج فقط.',
      submit: 'إرسال طلب العرض',
      sending: 'جارٍ الإرسال…',
      close: 'إغلاق',
      successTitle: 'تم تجهيز طلبك',
      successBody: 'سنفتح تطبيق البريد لإرسال الطلب إلى فريق المبيعات. لن يتم اعتبار الطلب شراءً أو دفعًا حتى يصلك عرض رسمي.',
      successBodyApi: 'تم استلام طلبك وسيتواصل معك فريق المبيعات قريبًا. لن يتم اعتبار الطلب شراءً أو دفعًا حتى يصلك عرض رسمي.',
      error: 'تعذر إرسال الطلب الآن. يمكنك التواصل مباشرة عبر البريد الظاهر أسفل الموقع.',
    },
  },
  en: {
    brandTagline: 'Dental laboratory management',
    nav: [
      { label: 'Features', href: '/features' },
      { label: 'How it works', href: '/workflow' },
      { label: 'Security', href: '/security' },
      { label: 'Plans', href: '/pricing' },
      { label: 'FAQ', href: '/faq' },
    ],
    headerCta: 'Request a quote',
    hero: {
      eyebrow: 'One platform. Clearer operations. A stronger lab.',
      titleBefore: 'Run your dental lab',
      titleAccent: 'with clarity',
      titleAfter: '',
      description:
        'Connect cases, finance, inventory and people in one system built around real dental-lab operations — from intake to delivery and collection.',
      primaryCta: 'Request a tailored quote',
      secondaryCta: 'Explore the platform',
      note: 'Guided setup • Data migration • Technical support',
    },
    trust: [
      { value: 'Local + remote', label: 'Flexible deployment' },
      { value: 'Granular access', label: 'Role-based permissions' },
      { value: 'Live updates', label: 'One shared source of truth' },
      { value: 'Arabic-first', label: 'Fast, familiar workflows' },
    ],
    features: {
      eyebrow: 'Everything your lab needs',
      title: 'One connected system, not scattered tools',
      description: 'Connected modules reduce duplicate work and give your team the context needed to move every case forward.',
      items: [
        { icon: 'tooth', title: 'Case production', description: 'Track every case from intake to final delivery.', points: ['Stages and due dates', 'Dentist and patient', 'Technician assignment'] },
        { icon: 'invoice', title: 'Billing and finance', description: 'Know what is paid, due and moving across every account.', points: ['Professional invoices', 'Payments and balances', 'Financial reports'] },
        { icon: 'inventory', title: 'Inventory and purchasing', description: 'Stay ahead of shortages and supplier commitments.', points: ['Stock alerts', 'Supplier purchasing', 'Material movements'] },
        { icon: 'team', title: 'People and permissions', description: 'Give each person the right access and responsibility.', points: ['Custom roles', 'Attendance and payroll', 'Technician commission'] },
        { icon: 'reports', title: 'Decision-ready reports', description: 'Turn daily activity into practical management insight.', points: ['Production output', 'Revenue and collection', 'Export and print'] },
        { icon: 'devices', title: 'Desktop and mobile', description: 'Stay close to operations inside and outside the lab.', points: ['Desktop client', 'Mobile access', 'Live synchronization'] },
      ],
    },
    showcase: {
      eyebrow: 'Your lab command center',
      title: 'The information that matters, minus the noise',
      description: 'See urgent cases, collection, output and team performance in one clear operational view.',
      tabs: ['Dashboard', 'Cases', 'Finance', 'Inventory'],
      bullets: ['Live daily view', 'Fast search and filters', 'Timely alerts'],
      cta: 'Explore all capabilities',
    },
    chart: {
      eyebrow: 'Tooth chart',
      title: 'Precision down to a single tooth',
      description:
        'Every case is recorded on an FDI-numbered tooth chart: restoration type, shade and notes stay attached to the exact tooth instead of one free-text field.',
      points: [
        { icon: 'tooth', title: 'Full FDI numbering', description: 'Upper and lower arches in the notation every dentist reads.' },
        { icon: 'reports', title: 'Detail per tooth', description: 'Type, shade and notes stored against the specific tooth.' },
        { icon: 'invoice', title: 'Pricing that follows', description: 'Unit counts flow straight into the invoice with no manual tally.' },
      ],
      hint: 'Hover any tooth',
      unitLabel: 'units',
      restorations: ['Zirconia', 'E-max', 'PFM', 'Veneer'],
    },
    team: {
      eyebrow: 'Collaboration without gaps',
      title: 'One flow across the whole team',
      description:
        'Dentists and lab staff work from the same record, with every stage tracked from submission through to delivery.',
      steps: [
        { icon: 'users', role: 'Dentist', action: 'Submits the case' },
        { icon: 'reports', role: 'Lab management', action: 'Receives and reviews' },
        { icon: 'tooth', role: 'Technician', action: 'Designs and produces' },
        { icon: 'inventory', role: 'Delivery', action: 'Ready to hand over' },
      ],
    },
    workflow: {
      eyebrow: 'A connected workflow',
      title: 'From case intake to confident delivery',
      description: 'Every step keeps its context and reaches the right person automatically.',
      steps: [
        { number: '01', title: 'Capture the case', description: 'Dentist, patient, product and due date in one structured intake.' },
        { number: '02', title: 'Plan and assign', description: 'Prioritize work and assign the right technicians.' },
        { number: '03', title: 'Produce and review', description: 'Update stages, attach images and document notes.' },
        { number: '04', title: 'Deliver and collect', description: 'Create the invoice, record payment and update balances.' },
      ],
    },
    security: {
      eyebrow: 'Built for real operations',
      title: 'Your lab data stays under your control',
      description: 'A client-server architecture, role-based access and encrypted connectivity put continuity and privacy at the center.',
      points: [
        { icon: 'shield', title: 'Role-based permissions', description: 'Control exactly what each person can view or change.' },
        { icon: 'server', title: 'Central server', description: 'One source of truth across every connected workstation.' },
        { icon: 'lock', title: 'Protected access', description: 'HTTPS support for secure access inside and outside the lab.' },
        { icon: 'backup', title: 'Operational readiness', description: 'Backup, update and crash-reporting tooling.' },
      ],
    },
    pricing: {
      eyebrow: 'Flexible plans',
      title: 'Start with what fits your lab',
      description: 'Final pricing reflects devices, branches, migration and support. No hidden fees and no vague promises.',
      featuredLabel: 'Best for growing labs',
      quote: 'Tailored quote',
      quoteNote: 'Based on size and deployment',
      currency: 'EGP',
      perMonth: 'per month',
      annualNote: 'save 2 months',
      cta: 'Request this plan',
      compare: 'Need a detailed comparison? Talk to us',
      plans: [
        { id: 'essential', name: 'Essential', audience: 'For a small lab', description: 'Organize daily cases and billing in one place.', features: ['Case management', 'Clients and invoices', 'Core reports', 'Up to 5 users', 'Initial setup and training'], price: { monthly: 800, yearly: 8000 } },
        { id: 'professional', name: 'Professional', audience: 'For a growing lab', description: 'Connect operations, resources and people with deeper control.', features: ['Everything in Essential', 'Inventory and purchasing', 'People and commissions', 'Advanced permissions', 'Up to 15 users', 'Mobile access'], featured: true, price: { monthly: 2000, yearly: 20000 } },
        { id: 'enterprise', name: 'Enterprise', audience: 'For large teams and branches', description: 'Deployment and support shaped around multi-site operations.', features: ['Everything in Professional', 'Multi-branch setup', 'Tailored data migration', 'Priority support', 'Custom integrations'] },
      ],
    },
    purchase: {
      title: 'A clear and secure buying journey',
      description: 'We never request card data before an approved payment provider is active. The journey starts with a transparent, tailored scope.',
      steps: [
        { icon: 'plan', title: 'Choose a plan', description: 'Share your size and needs' },
        { icon: 'chat', title: 'Review the quote', description: 'Clear price and scope' },
        { icon: 'payment', title: 'Pay securely', description: 'Through an approved provider' },
        { icon: 'key', title: 'Receive and activate', description: 'License and guided setup' },
      ],
    },
    faq: {
      eyebrow: 'Before you start',
      title: 'Direct answers to important questions',
      description: 'If your question is different, send it over and we will shape the proposal around your lab.',
      items: [
        { question: 'Can the system run without internet?', answer: 'The platform can run on the lab network through a local server. Remote access and update services require internet connectivity.' },
        { question: 'Can you migrate our existing data?', answer: 'Yes. We first review the source format, then scope and validate the migration so operating history is preserved.' },
        { question: 'Does it support multiple users and devices?', answer: 'Yes. The client-server architecture keeps the team on one source of truth with the right permissions for each role.' },
        { question: 'How does licensing and activation work?', answer: 'After agreement and payment, a signed license is issued for the approved plan and securely activated on the lab server.' },
        { question: 'Is a trial available?', answer: 'A guided demo or trial license can be arranged based on the lab environment and requested scope.' },
      ],
    },
    finalCta: {
      eyebrow: 'Start with your lab reality',
      title: 'Turn the working day into a clear system',
      description: 'Tell us about your users, branches and current workflow. We will recommend a practical rollout plan.',
      primary: 'Request a tailored quote',
      secondary: 'Email sales',
    },
    footer: {
      description: 'A connected operating platform for dental labs — production, finance, inventory and people in one experience.',
      product: 'Product',
      company: 'Contact',
      legal: 'Legal',
      links: ['Features', 'How it works', 'Plans', 'FAQ'],
      copyright: 'All rights reserved.',
      status: 'Operational systems available',
    },
    form: {
      eyebrow: 'Sales request',
      title: 'Let us shape the right plan for your lab',
      description: 'Share the essentials and our team will review requirements, pricing and rollout with you.',
      selectedPlan: 'Selected plan',
      name: 'Full name',
      lab: 'Laboratory name',
      email: 'Work email',
      phone: 'Phone number',
      country: 'Country',
      users: 'Expected users',
      notes: 'What matters most to improve?',
      privacy: 'By sending this request, you agree that we use the details only to contact you about the product.',
      submit: 'Send quote request',
      sending: 'Sending…',
      close: 'Close',
      successTitle: 'Your request is ready',
      successBody: 'Your mail app will open to send the request to sales. This is not a purchase or payment until you receive a formal quote.',
      successBodyApi: 'We have received your request and the sales team will contact you soon. This is not a purchase or payment until you receive a formal quote.',
      error: 'We could not send the request. You can email the sales address shown in the footer.',
    },
  },
};
